import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { ChatListRemoteResults } from "@altek/ui/components/chat/ChatList";
import { chatApi } from "../api/chat.api";
import { MESSAGE_SEARCH_MIN_CHARS, chatMessageSearchQuery } from "../api/chat.queries";
import type { ChatConversationRow } from "../api/chat.types";
import { chatErrorMessage } from "../lib/chat-errors";
import { conversationEntry, conversationTitle, searchHitItem } from "../lib/chat-map";
import { keys } from "@/lib/api/query-keys";
import { STALE } from "@/lib/api/query-client";
import { useDebouncedValue } from "./useDebouncedValue";

/** How long the rail waits after the last keystroke before it asks the server. */
const SEARCH_SETTLE_MS = 300;

/**
 * RAYIN ARAMA KUTUSU MESAJLARI DA BULUYOR (sohbet-duzeltme 20 Ekim 2026, denetim H6 — ölçüldü:
 * «Konuşmalarda ara» bir mesaj kelimesine «Sonuç yok» diyordu; `/chat/messages/search` vardı ama
 * ekran onu hiç çağırmıyordu).
 *
 * Kitin `ChatList`'i yüklü sayfayı KENDİSİ süzüyor; bu kanca onun `remoteResults`'ını dolduruyor:
 *  - «Mesajlar»: `GET /chat/messages/search` — terim iki harfe ulaşınca ve yazma durunca bir kez;
 *    her vuruş hangi sohbette olduğunu adıyla söylüyor (sunucu adlandırıyor, rayda yüklü değilse de);
 *  - «Diğer sohbetler»: `GET /chat/conversations?q=` — rayın 50'lik sayfasının ötesindeki sohbet
 *    adları ve üye adları (kit yüklü olanları ikinci kez yazmıyor).
 * `query` alanı sonuçların CEVAPLADIĞI terim: kit, kutudaki terimle eşleşmeyen sonuçları çizmiyor —
 * «irs»in cevabı «irsaliye»nin altında eşleşme gibi durmuyor.
 */
export function useChatSearch(input: {
  term: string;
  enabled: boolean;
  meId: string;
  /** The rail's loaded rows: a hit in one of them takes the name the rail gives it («(Siz)»). */
  loadedRows: readonly ChatConversationRow[];
  isPinned: (conversationId: string) => boolean;
}): ChatListRemoteResults | undefined {
  const settled = useDebouncedValue(input.term.trim(), SEARCH_SETTLE_MS);
  const asking = input.enabled && settled.length >= MESSAGE_SEARCH_MIN_CHARS;

  const messagesQuery = useQuery(chatMessageSearchQuery(settled, { enabled: asking }));
  const conversationsQuery = useQuery({
    queryKey: keys.chat.conversations({ q: settled, limit: 20, scope: "search" }),
    queryFn: () => chatApi.conversations({ q: settled, limit: 20 }),
    staleTime: STALE.live,
    enabled: asking,
    placeholderData: undefined as never,
  });

  const loadedNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const row of input.loadedRows) names.set(row.id, conversationTitle(row, input.meId) ?? row.title);
    return names;
  }, [input.loadedRows, input.meId]);

  const { isPinned, meId } = input;
  const conversations = useMemo(
    () =>
      (conversationsQuery.data?.rows ?? []).map((row) => conversationEntry(row, { meId, pinned: isPinned(row.id) })),
    [conversationsQuery.data, meId, isPinned],
  );
  const messages = useMemo(
    () =>
      (Array.isArray(messagesQuery.data) ? messagesQuery.data : []).map((hit) =>
        searchHitItem(hit, loadedNames.get(hit.conversationId)),
      ),
    [messagesQuery.data, loadedNames],
  );

  if (!asking) return undefined;
  const failure = messagesQuery.error ?? conversationsQuery.error;
  return {
    query: settled,
    conversations,
    messages,
    isLoading: messagesQuery.isFetching || conversationsQuery.isFetching,
    error: failure ? chatErrorMessage(failure) : null,
    onRetry: () => {
      void messagesQuery.refetch();
      void conversationsQuery.refetch();
    },
  };
}
